import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { fmtDateTime } from "../lib/format";
import {
  Plus, FileText, Download, Trash2, Loader2, X, ChevronLeft, ChevronRight,
  CheckCircle2, AlertTriangle, Search, Users, Eye, FileSearch,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: "all", label: "All", testid: "all" },
  { id: "player_profile", label: "Profiles", testid: "profiles" },
  { id: "portfolio", label: "Portfolios", testid: "portfolios" },
  { id: "progress", label: "Progress", testid: "progress" },
  { id: "scouting_brief", label: "Scouting", testid: "scouting" },
  { id: "mandate_portfolio", label: "Mandate Register", testid: "mandate" },
  { id: "analytics", label: "Analytics", testid: "analytics" },
];

const LANGUAGES = [
  { id: "en", label: "English" }, { id: "nl", label: "Nederlands" },
  { id: "es", label: "Español" },  { id: "fr", label: "Français" },
  { id: "de", label: "Deutsch" },  { id: "it", label: "Italiano" },
];

const THEMES = [
  { id: "classic",     name: "Classic",     desc: "Near-black · signal-green accent · for top clubs",         tone: "bg-slate-900 text-emerald-300" },
  { id: "modern",      name: "Modern",      desc: "White · clean sections · everyday professional",          tone: "bg-white text-slate-800 border border-slate-200" },
  { id: "futuristic",  name: "Futuristic",  desc: "Light grey · whitespace-heavy · luxury feel",             tone: "bg-stone-100 text-stone-800" },
];

export default function Reports() {
  const [tab, setTab] = useState("all");
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [builderOpen, setBuilderOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports");
      setReports(data || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const visible = useMemo(() => {
    if (tab === "all" || tab === "analytics") return reports;
    return reports.filter((r) => r.report_type === tab);
  }, [reports, tab]);

  const previewReport = async (r) => {
    setSelected(r);
    if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl);
    setPdfBlobUrl(null);
    const token = localStorage.getItem("pm_token");
    const res = await fetch(`${API}/reports/${r.id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return;
    const blob = await res.blob();
    setPdfBlobUrl(URL.createObjectURL(blob));
  };

  const downloadReport = async (r) => {
    const token = localStorage.getItem("pm_token");
    const res = await fetch(`${API}/reports/${r.id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = r.filename || `report-${r.id}.pdf`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  const deleteReport = async (r) => {
    if (!confirm(`Delete "${r.title || r.client_name || r.id}"?`)) return;
    await api.delete(`/reports/${r.id}`);
    if (selected?.id === r.id) { setSelected(null); setPdfBlobUrl(null); }
    load();
  };

  return (
    <Layout title="Reports" subtitle="Player profiles · Agency portfolios · Scouting briefs">
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left — list */}
        <div className="lg:col-span-2 flex flex-col gap-4">
          <div className="flex items-center gap-2">
            <div className="flex-1 border-b border-slate-200">
              {TABS.map((t) => (
                <button key={t.id} onClick={() => setTab(t.id)} data-testid={`reports-tab-${t.testid}`}
                  className={`px-3 py-2.5 text-sm font-bold tracking-tight border-b-2 transition-all ${
                    tab === t.id ? "border-emerald-500 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"
                  }`}>{t.label}</button>
              ))}
            </div>
            <button onClick={() => setBuilderOpen(true)}
              className="pm-btn-primary h-9 px-3 rounded-lg text-[12px] font-bold flex items-center gap-1.5 shrink-0"
              data-testid="reports-new-btn">
              <Plus size={12} /> New report
            </button>
            <Link to="/settings/reports"
              className="pm-btn-secondary h-9 px-3 rounded-lg text-[12px] font-bold flex items-center gap-1.5 shrink-0"
              data-testid="reports-branding-link">
              Branding
            </Link>
          </div>

          <div className="space-y-2" data-testid="reports-list">
            {loading && <div className="text-center text-slate-500 py-8"><Loader2 size={14} className="inline animate-spin mr-2" /> Loading…</div>}
            {!loading && visible.length === 0 && (
              <div className="pm-card p-10 text-center text-slate-500">
                <FileSearch size={28} className="mx-auto mb-3 opacity-40" />
                <div className="text-sm">No reports yet. Click <b>New report</b> to generate one.</div>
              </div>
            )}
            {visible.map((r) => (
              <div key={r.id} onClick={() => previewReport(r)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border cursor-pointer transition-all ${
                  selected?.id === r.id ? "border-emerald-300 bg-emerald-50/50" : "border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/30"
                }`}
                data-testid={`report-row-${r.id}`}>
                <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                  <FileText size={14} className="text-slate-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold truncate">{r.title || r.client_name || "Untitled"}</div>
                  <div className="text-[11px] text-slate-500 flex items-center gap-2">
                    <span className="capitalize">{(r.report_type || "").replace("_", " ")}</span>
                    <span>·</span>
                    <span className="capitalize">{r.theme}</span>
                    <span>·</span>
                    <span>{r.page_count}p</span>
                    <span>·</span>
                    <span>{fmtDateTime(r.created_at).slice(0, 11)}</span>
                  </div>
                </div>
                <button onClick={(e) => { e.stopPropagation(); downloadReport(r); }}
                  className="pm-btn-secondary h-7 w-7 rounded-lg flex items-center justify-center" data-testid={`report-download-${r.id}`}>
                  <Download size={11} />
                </button>
                <button onClick={(e) => { e.stopPropagation(); deleteReport(r); }}
                  className="pm-btn-secondary h-7 w-7 rounded-lg flex items-center justify-center hover:bg-rose-50 hover:text-rose-700" data-testid={`report-delete-${r.id}`}>
                  <Trash2 size={11} />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Right — preview / analytics */}
        <div className="lg:col-span-3">
          {tab === "analytics" ? (
            <AnalyticsPanel />
          ) : (
          <div className="pm-card overflow-hidden h-[calc(100vh-200px)] flex flex-col" data-testid="reports-preview-panel">
            {!selected && (
              <div className="flex-1 flex flex-col items-center justify-center text-slate-500 text-sm">
                <Eye size={36} className="mb-3 opacity-30" />
                <div>Click a report to preview</div>
              </div>
            )}
            {selected && (
              <>
                <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-bold tracking-tight truncate">{selected.title || selected.client_name}</div>
                    <div className="text-[11px] text-slate-500 capitalize">{(selected.report_type || "").replace("_"," ")} · {selected.theme} · {selected.page_count}p</div>
                  </div>
                  <button onClick={() => downloadReport(selected)}
                    className="pm-btn-secondary h-8 px-3 rounded-lg text-[11px] font-bold flex items-center gap-1.5" data-testid="preview-download-btn">
                    <Download size={11} /> Download
                  </button>
                </div>
                <div className="flex-1 bg-slate-100">
                  {pdfBlobUrl ? (
                    <iframe title="report-preview" src={pdfBlobUrl} className="w-full h-full" data-testid="report-pdf-iframe" />
                  ) : (
                    <div className="flex items-center justify-center h-full text-slate-500 text-sm"><Loader2 size={13} className="animate-spin mr-2" /> Loading PDF…</div>
                  )}
                </div>
              </>
            )}
          </div>
          )}
        </div>
      </div>

      {builderOpen && (
        <ReportBuilder
          onClose={() => setBuilderOpen(false)}
          onSuccess={(r) => { setBuilderOpen(false); load(); setTimeout(() => previewReport(r), 200); }}
        />
      )}
    </Layout>
  );
}

// ─────────────────────────── Builder ───────────────────────────

const REPORT_TYPES = [
  { id: "player_profile",    title: "Player Profile",    desc: "Single player. 2-4 pages. Works for all three tiers. Send to clubs proposing a player.",   color: "emerald" },
  { id: "portfolio",         title: "Agency Portfolio",  desc: "Multi-player. Mix all tiers. Position-based or hand-picked. Open the transfer window with this.", color: "blue" },
  { id: "progress",          title: "Player Progress",   desc: "Single Tier-1 client · two time periods compared side by side. Send to player / family / tracking club.", color: "amber" },
  { id: "scouting_brief",    title: "Scouting Brief",    desc: "3-5 candidates compared side by side. Send to a TD or to player agents.", color: "indigo" },
  { id: "mandate_portfolio", title: "Mandate Register",  desc: "All outgoing mandates in one document. Internal compliance record — not sent to clubs.", color: "rose" },
];

function ReportBuilder({ onClose, onSuccess }) {
  const [step, setStep] = useState(1);
  const [reportType, setReportType] = useState(null);

  // Single player
  const [singleClientId, setSingleClientId] = useState(null);

  // Portfolio
  const [portfolioMode, setPortfolioMode] = useState("position");
  const [posGroups, setPosGroups] = useState([]);
  const [posIndividual, setPosIndividual] = useState([]);
  const [tierFilter, setTierFilter] = useState(["client", "network", "mandated"]);
  const [manualSelected, setManualSelected] = useState([]);
  const [previewCount, setPreviewCount] = useState(null);
  const [previewPlayers, setPreviewPlayers] = useState([]);
  const [portfolioLayout, setPortfolioLayout] = useState("full_page");
  const [customTitle, setCustomTitle] = useState("");

  // Step 3
  const [theme, setTheme] = useState("classic");
  const [language, setLanguage] = useState("en");
  const [recipient, setRecipient] = useState("");
  const [confidential, setConfidential] = useState(false);
  const [agentNotes, setAgentNotes] = useState("");

  // Progress periods
  const [thisPeriodLabel, setThisPeriodLabel] = useState("2025/26");
  const [lastPeriodLabel, setLastPeriodLabel] = useState("2024/25");
  const [thisPeriod, setThisPeriod] = useState({ apps: "", mins: "", goals: "", assists: "", xg: "", xa: "" });
  const [lastPeriod, setLastPeriod] = useState({ apps: "", mins: "", goals: "", assists: "", xg: "", xa: "" });

  // Branding-driven defaults
  useEffect(() => {
    api.get("/workspaces/mine").then((r) => {
      const ws = Array.isArray(r.data) ? r.data[0] : (r.data?.workspaces?.[0] || r.data);
      const branding = ws?.branding || {};
      if (branding.default_theme) setTheme(branding.default_theme);
      if (branding.default_language) setLanguage(branding.default_language);
    }).catch(() => {});
  }, []);
  const [sections, setSections] = useState({
    market_value:      true,
    season_stats:      true,
    performance_radar: true,
    career_history:    true,
    agent_notes:       true,
    video_highlights:  false,
    contract_summary:  false,
    mandate_details:   true,
  });
  const [videoUrl, setVideoUrl] = useState("");

  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);

  const [positionGroupsMeta, setPositionGroupsMeta] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientSearch, setClientSearch] = useState("");

  useEffect(() => {
    api.get("/reports/meta/position-groups").then((r) => setPositionGroupsMeta(r.data.groups || []));
    api.get("/clients").then((r) => setClients(r.data || []));
  }, []);

  const refreshPreview = async () => {
    setPreviewCount(null);
    if (reportType !== "portfolio") return;
    try {
      const payload = {
        portfolio_mode: portfolioMode,
        position_groups: posGroups,
        position_filter: posIndividual,
        tier_filter: tierFilter,
        client_ids: portfolioMode === "manual" ? manualSelected : [],
      };
      const { data } = await api.post("/reports/preview-portfolio", payload);
      setPreviewCount(data.count);
      setPreviewPlayers(data.players || []);
    } catch (e) {
      setPreviewCount(0);
      setPreviewPlayers([]);
    }
  };
  useEffect(() => {
    refreshPreview();
    // eslint-disable-next-line
  }, [reportType, portfolioMode, posGroups.join(","), posIndividual.join(","), tierFilter.join(","), manualSelected.join(",")]);

  const togglePosGroup = (key) => {
    setPosGroups((g) => g.includes(key) ? g.filter((x) => x !== key) : [...g, key]);
  };
  const togglePosIndividual = (p) => {
    setPosIndividual((arr) => arr.includes(p) ? arr.filter((x) => x !== p) : [...arr, p]);
  };
  const toggleTier = (t) => {
    setTierFilter((arr) => arr.includes(t) ? arr.filter((x) => x !== t) : [...arr, t]);
  };
  const toggleManualClient = (id) => {
    setManualSelected((arr) => arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);
  };

  const filteredClients = useMemo(() => {
    const q = clientSearch.trim().toLowerCase();
    return clients.filter((c) => !q || (c.full_name || "").toLowerCase().includes(q) || (c.current_club || "").toLowerCase().includes(q));
  }, [clients, clientSearch]);

  const canContinue = (() => {
    if (step === 1) return !!reportType;
    if (step === 2) {
      if (reportType === "player_profile" || reportType === "progress") return !!singleClientId;
      if (reportType === "portfolio") {
        if (portfolioMode === "manual") return manualSelected.length > 0;
        if (portfolioMode === "full_roster") return (previewCount || 0) > 0;
        return (previewCount || 0) > 0;
      }
      if (reportType === "scouting_brief") return manualSelected.length >= 2 && manualSelected.length <= 5;
      if (reportType === "mandate_portfolio") return true;
    }
    return true;
  })();

  const generate = async () => {
    setGenerating(true); setError(null);
    try {
      const baseSections = sections;
      const payload = {
        report_type: reportType,
        theme, language,
        recipient_name: recipient || null,
        custom_title: customTitle || null,
        show_confidential: confidential,
        video_url: videoUrl || null,
        sections: baseSections,
      };
      let endpoint = "/reports";
      if (reportType === "player_profile") {
        payload.client_id = singleClientId;
        payload.agent_notes_single = agentNotes || null;
      } else if (reportType === "portfolio") {
        payload.portfolio_mode = portfolioMode;
        payload.portfolio_layout = portfolioLayout;
        payload.position_groups = posGroups;
        payload.position_filter = posIndividual;
        payload.tier_filter = tierFilter;
        payload.client_ids = portfolioMode === "manual" ? manualSelected : [];
      } else if (reportType === "progress") {
        endpoint = "/reports/progress";
        payload.client_id = singleClientId;
        payload.agent_notes_single = agentNotes || null;
        const toNum = (s) => (s === "" || s === null || s === undefined ? null : (Number(s) || 0));
        payload.sections = {
          ...baseSections,
          this_period: { label: thisPeriodLabel, ...Object.fromEntries(Object.entries(thisPeriod).map(([k, v]) => [k, toNum(v)])) },
          last_period: { label: lastPeriodLabel, ...Object.fromEntries(Object.entries(lastPeriod).map(([k, v]) => [k, toNum(v)])) },
        };
      } else if (reportType === "scouting_brief") {
        endpoint = "/reports/scouting-brief";
        payload.client_ids = manualSelected;
        payload.agent_notes = {};
      } else if (reportType === "mandate_portfolio") {
        endpoint = "/reports/mandate-portfolio";
      }
      const { data } = await api.post(endpoint, payload);
      onSuccess && onSuccess(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not generate report");
    } finally {
      setGenerating(false);
    }
  };

  const next = () => setStep((s) => Math.min(4, s + 1));
  const back = () => setStep((s) => Math.max(1, s - 1));

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-sm flex items-stretch justify-center p-4 overflow-y-auto" data-testid="report-builder">
      <div className="bg-white w-full max-w-5xl rounded-2xl my-4 flex flex-col max-h-[calc(100vh-2rem)]">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-3">
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">New report</div>
            <div className="font-display text-lg font-bold tracking-tight">Step {step} of 4 — {step === 1 ? "Select type" : step === 2 ? "Choose player(s)" : step === 3 ? "Configure" : "Preview & generate"}</div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center" data-testid="builder-close-btn">
            <X size={14} />
          </button>
        </div>

        <div className="flex-1 p-6 overflow-y-auto">
          {/* STEP 1 */}
          {step === 1 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="builder-step-1">
              {REPORT_TYPES.map((rt) => (
                <button key={rt.id} onClick={() => !rt.disabled && setReportType(rt.id)}
                  disabled={rt.disabled}
                  aria-disabled={rt.disabled ? "true" : undefined}
                  className={`text-left p-5 rounded-xl border-2 transition-all ${
                    reportType === rt.id ? "border-emerald-500 bg-emerald-50/40 shadow-sm" :
                    rt.disabled ? "border-slate-200 bg-slate-50 opacity-50 cursor-not-allowed pointer-events-none" :
                    "border-slate-200 hover:border-emerald-300"
                  }`}
                  data-testid={`type-${rt.id}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <h4 className="font-display text-lg font-bold tracking-tight">{rt.title}</h4>
                    {rt.badge && <span className="pm-pill pm-pill-slate text-[9px]">{rt.badge}</span>}
                  </div>
                  <p className="text-[12px] text-slate-600 leading-relaxed">{rt.desc}</p>
                </button>
              ))}
            </div>
          )}

          {/* STEP 2 */}
          {step === 2 && (reportType === "player_profile" || reportType === "progress") && (
            <div data-testid="builder-step-2-single">
              <div className="relative mb-3">
                <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input className="pm-input pl-9 w-full" placeholder="Search player by name or club…"
                  value={clientSearch} onChange={(e) => setClientSearch(e.target.value)} data-testid="single-client-search" />
              </div>
              {reportType === "progress" && (
                <div className="text-[11px] text-amber-700 mb-3 flex items-center gap-1.5">
                  <AlertTriangle size={11} /> Progress reports are intended for Tier-1 clients with comparable season data.
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-[60vh] overflow-y-auto pr-1">
                {filteredClients.filter((c) => reportType !== "progress" || (c.tier || "client") === "client").map((c) => (
                  <button key={c.id} onClick={() => setSingleClientId(c.id)}
                    className={`flex items-center gap-3 p-2.5 rounded-xl border text-left transition-all ${
                      singleClientId === c.id ? "border-emerald-500 bg-emerald-50/40" : "border-slate-200 hover:border-emerald-300"
                    }`} data-testid={`single-client-${c.id}`}>
                    <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-[11px] font-bold text-slate-600 shrink-0">
                      {(c.full_name || "?").slice(0, 1)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold truncate">{c.full_name}</div>
                      <div className="text-[11px] text-slate-500 truncate">{c.position || "—"} · {c.current_club || "—"}</div>
                    </div>
                    <TierBadge tier={c.tier} />
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 2 && reportType === "scouting_brief" && (
            <div data-testid="builder-step-2-scouting" className="space-y-3">
              <div className="text-[12px] text-slate-600">Pick 2-5 candidates to compare side by side.</div>
              <TierFilterRow tiers={tierFilter} onToggle={toggleTier} />
              <div className="text-[11px] text-slate-500">Selected: <b>{manualSelected.length}</b> / 5</div>
              <div className="relative">
                <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input className="pm-input pl-9 w-full" placeholder="Search player…"
                  value={clientSearch} onChange={(e) => setClientSearch(e.target.value)} data-testid="scouting-search" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5 max-h-[50vh] overflow-y-auto pr-1">
                {filteredClients.filter((c) => tierFilter.includes(c.tier || "client")).map((c) => {
                  const isSel = manualSelected.includes(c.id);
                  const disabled = !isSel && manualSelected.length >= 5;
                  return (
                    <label key={c.id} className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer ${
                      isSel ? "border-emerald-500 bg-emerald-50/40" :
                      disabled ? "border-slate-200 opacity-40 cursor-not-allowed" :
                      "border-slate-200 hover:border-emerald-300"
                    }`} data-testid={`scouting-client-${c.id}`}>
                      <input type="checkbox" checked={isSel} disabled={disabled} onChange={() => toggleManualClient(c.id)} className="accent-emerald-600" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold truncate">{c.full_name}</div>
                        <div className="text-[10px] text-slate-500 truncate">{c.position || "—"} · {c.current_club || "—"}</div>
                      </div>
                      <TierBadge tier={c.tier} />
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {step === 2 && reportType === "mandate_portfolio" && (
            <div data-testid="builder-step-2-mandate" className="rounded-xl border border-slate-200 p-5">
              <div className="text-sm font-bold mb-2">Mandate Register</div>
              <div className="text-[12px] text-slate-600 leading-relaxed">
                This report lists every <b>outgoing mandate</b> in your current workspace — players you have mandated to other agents — with their scope and expiry.
                It is an internal compliance record. <b>Commission splits are never shown.</b> No selection required — just configure the theme on Step 3 and generate.
              </div>
            </div>
          )}

          {step === 2 && reportType === "portfolio" && (
            <div data-testid="builder-step-2-portfolio" className="space-y-4">
              {/* Mode tabs */}
              <div className="border-b border-slate-200 flex">
                {[
                  { id: "position",    label: "By position" },
                  { id: "manual",      label: "Manual selection" },
                  { id: "full_roster", label: "Full roster" },
                ].map((m) => (
                  <button key={m.id} onClick={() => setPortfolioMode(m.id)} data-testid={`mode-${m.id}`}
                    className={`px-4 py-2.5 text-sm font-bold tracking-tight border-b-2 transition-all ${
                      portfolioMode === m.id ? "border-emerald-500 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"
                    }`}>{m.label}</button>
                ))}
              </div>

              {portfolioMode === "position" && (
                <div className="space-y-4">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-2">Position groups</div>
                    <div className="flex flex-wrap gap-2">
                      {positionGroupsMeta.map((g) => (
                        <button key={g.key} onClick={() => togglePosGroup(g.key)} data-testid={`group-${g.key}`}
                          className={`px-3 py-1.5 rounded-lg border text-sm font-bold tracking-tight ${
                            posGroups.includes(g.key) ? "border-emerald-500 bg-emerald-50 text-emerald-800" : "border-slate-200 hover:border-emerald-300"
                          }`}>
                          {g.label} {posGroups.includes(g.key) && <CheckCircle2 size={11} className="inline ml-1" />}
                        </button>
                      ))}
                    </div>
                  </div>

                  {posGroups.length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-2">Individual positions (fine-tune)</div>
                      <div className="flex flex-wrap gap-2">
                        {positionGroupsMeta.filter((g) => posGroups.includes(g.key)).flatMap((g) => g.positions).map((p) => (
                          <button key={p} onClick={() => togglePosIndividual(p)} data-testid={`pos-${p}`}
                            className={`px-2.5 py-1 rounded-md border text-[11px] font-bold ${
                              posIndividual.includes(p) ? "border-emerald-500 bg-emerald-50 text-emerald-800" : "border-slate-200"
                            }`}>{p}</button>
                        ))}
                      </div>
                      <div className="text-[10px] text-slate-500 mt-1.5">Leave all blank to include every position in the selected group(s).</div>
                    </div>
                  )}

                  <TierFilterRow tiers={tierFilter} onToggle={toggleTier} />

                  <PreviewSummary count={previewCount} players={previewPlayers} />
                </div>
              )}

              {portfolioMode === "manual" && (
                <div className="space-y-3">
                  <TierFilterRow tiers={tierFilter} onToggle={toggleTier} />
                  <div className="text-[11px] text-slate-500">Selected: <b>{manualSelected.length}</b></div>
                  <div className="relative">
                    <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input className="pm-input pl-9 w-full" placeholder="Search player by name or club…"
                      value={clientSearch} onChange={(e) => setClientSearch(e.target.value)} data-testid="manual-search" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5 max-h-[40vh] overflow-y-auto pr-1">
                    {filteredClients.filter((c) => tierFilter.includes(c.tier || "client") && c.can_appear_in_portfolio !== false).map((c) => (
                      <label key={c.id} className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer ${
                        manualSelected.includes(c.id) ? "border-emerald-500 bg-emerald-50/40" : "border-slate-200 hover:border-emerald-300"
                      }`} data-testid={`manual-client-${c.id}`}>
                        <input type="checkbox" checked={manualSelected.includes(c.id)} onChange={() => toggleManualClient(c.id)} className="accent-emerald-600" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-bold truncate">{c.full_name}</div>
                          <div className="text-[10px] text-slate-500 truncate">{c.position || "—"} · {c.current_club || "—"}</div>
                        </div>
                        <TierBadge tier={c.tier} />
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {portfolioMode === "full_roster" && (
                <div className="space-y-3">
                  <TierFilterRow tiers={tierFilter} onToggle={toggleTier} />
                  <PreviewSummary count={previewCount} players={previewPlayers} />
                  <div className="text-[12px] text-slate-500">All eligible players will be included (i.e. <code>can_appear_in_portfolio = true</code>). You can deselect individuals on Step 3 by toggling them off in Manual mode.</div>
                </div>
              )}

              <div className="flex items-center gap-3 pt-3 border-t border-slate-200">
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Layout</div>
                {[
                  { id: "full_page",    label: "Full page (1 per page)" },
                  { id: "compact_grid", label: "Compact grid (2 per page)" },
                ].map((l) => (
                  <button key={l.id} onClick={() => setPortfolioLayout(l.id)} data-testid={`layout-${l.id}`}
                    className={`px-2.5 py-1 rounded-md border text-[11px] font-bold ${
                      portfolioLayout === l.id ? "border-emerald-500 bg-emerald-50 text-emerald-800" : "border-slate-200"
                    }`}>{l.label}</button>
                ))}
              </div>
            </div>
          )}

          {/* STEP 3 */}
          {step === 3 && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5" data-testid="builder-step-3">
              <div className="lg:col-span-2 space-y-4">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-2">Theme</div>
                  <div className="grid grid-cols-3 gap-2">
                    {THEMES.map((t) => (
                      <button key={t.id} onClick={() => setTheme(t.id)} data-testid={`theme-${t.id}`}
                        className={`p-3 rounded-xl text-left border-2 ${theme === t.id ? "border-emerald-500" : "border-slate-200"}`}>
                        <div className={`h-14 rounded-lg mb-2 flex items-center px-3 text-sm font-bold ${t.tone}`}>{t.name}</div>
                        <div className="text-[11px] text-slate-500">{t.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-2">Sections</div>
                  <div className="grid grid-cols-2 gap-1.5">
                    {Object.entries(sections).map(([k, v]) => (
                      <label key={k} className="flex items-center gap-2 p-2 rounded-lg border border-slate-200">
                        <input type="checkbox" checked={v} onChange={() => setSections({ ...sections, [k]: !v })} className="accent-emerald-600" data-testid={`section-${k}`} />
                        <span className="text-[12px] capitalize">{k.replaceAll("_", " ")}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {reportType === "player_profile" && (
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-2">Agent's assessment (max 800 chars)</div>
                    <textarea value={agentNotes} onChange={(e) => setAgentNotes(e.target.value.slice(0, 800))} rows={4}
                      placeholder="Two-footed left winger with elite acceleration. Available for summer move."
                      className="pm-input w-full text-[13px]" data-testid="agent-notes-input" />
                  </div>
                )}

                {sections.video_highlights && (
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-2">Video highlights URL</div>
                    <input value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)}
                      placeholder="https://www.wyscout.com/playlist/..."
                      className="pm-input w-full text-[13px]" data-testid="video-url-input" />
                  </div>
                )}
              </div>

              <div className="space-y-3">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-2">Language</div>
                  <select value={language} onChange={(e) => setLanguage(e.target.value)}
                    className="pm-input w-full text-[12px]" data-testid="language-select">
                    {LANGUAGES.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
                  </select>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-2">Recipient (optional)</div>
                  <input value={recipient} onChange={(e) => setRecipient(e.target.value)}
                    placeholder="AFC Ajax · scouting@ajax.com"
                    className="pm-input w-full text-[12px]" data-testid="recipient-input" />
                </div>
                {(reportType === "portfolio" || reportType === "scouting_brief" || reportType === "mandate_portfolio") && (
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-2">Custom cover title</div>
                    <input value={customTitle} onChange={(e) => setCustomTitle(e.target.value)}
                      placeholder={reportType === "scouting_brief" ? "Left wingers shortlist" : "Wingers — Summer 2026"}
                      className="pm-input w-full text-[12px]" data-testid="custom-title-input" />
                  </div>
                )}
                <label className="flex items-center gap-2 p-2 rounded-lg border border-slate-200">
                  <input type="checkbox" checked={confidential} onChange={() => setConfidential(!confidential)} className="accent-emerald-600" data-testid="confidential-toggle" />
                  <span className="text-[12px]">Confidential watermark</span>
                </label>
              </div>
            </div>
          )}

          {/* PROGRESS — period inputs surfaced on step 3 (after step 2 player pick) */}
          {step === 3 && reportType === "progress" && (
            <div className="rounded-xl border border-slate-200 p-4 mt-4" data-testid="progress-periods-block">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-3">Compare two periods</div>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: thisPeriodLabel, setLabel: setThisPeriodLabel, vals: thisPeriod, setVals: setThisPeriod, key: "this", title: "Current" },
                  { label: lastPeriodLabel, setLabel: setLastPeriodLabel, vals: lastPeriod, setVals: setLastPeriod, key: "last", title: "Previous" },
                ].map((p) => (
                  <div key={p.key} className="rounded-lg bg-slate-50 p-3">
                    <input value={p.label} onChange={(e) => p.setLabel(e.target.value)}
                      className="pm-input w-full text-[12px] mb-2 font-bold" placeholder={`${p.title} season label`}
                      data-testid={`progress-${p.key}-label`} />
                    {["apps", "mins", "goals", "assists", "xg", "xa"].map((k) => (
                      <div key={k} className="flex items-center gap-2 mb-1">
                        <span className="text-[11px] text-slate-500 w-16 capitalize">{k}</span>
                        <input value={p.vals[k]} onChange={(e) => p.setVals({ ...p.vals, [k]: e.target.value })}
                          type="number" step="0.01" className="pm-input flex-1 text-[12px] h-8"
                          data-testid={`progress-${p.key}-${k}`} />
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* STEP 4 */}
          {step === 4 && (
            <div className="space-y-4" data-testid="builder-step-4">
              <div className="rounded-xl border border-slate-200 p-4 bg-slate-50">
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-2">Ready to generate</div>
                <div className="space-y-1 text-[13px]">
                  <KV k="Type" v={(reportType || "").replace("_", " ")} />
                  <KV k="Theme" v={theme} />
                  {reportType === "player_profile" && <KV k="Player" v={clients.find((c) => c.id === singleClientId)?.full_name || "—"} />}
                  {reportType === "portfolio" && <KV k="Players" v={`${previewCount ?? "—"} · ${portfolioMode.replace("_", " ")} · ${portfolioLayout.replace("_", " ")}`} />}
                  {recipient && <KV k="Recipient" v={recipient} />}
                  {customTitle && <KV k="Title" v={customTitle} />}
                </div>
                <div className="text-[11px] text-slate-500 mt-3">
                  Generation typically takes 1-5 seconds for profiles, 10-60 seconds for large portfolios.
                </div>
              </div>
              {error && <div className="text-[12px] text-rose-700 flex items-center gap-1.5"><AlertTriangle size={11} /> {error}</div>}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-between sticky bottom-0 bg-white rounded-b-2xl">
          <button onClick={back} disabled={step === 1}
            className="pm-btn-secondary h-9 px-3 rounded-lg text-[12px] font-bold flex items-center gap-1.5 disabled:opacity-30 disabled:cursor-not-allowed"
            data-testid="builder-back-btn">
            <ChevronLeft size={11} /> Back
          </button>
          <div className="text-[11px] text-slate-500 font-mono-tab">Step {step} / 4</div>
          {step < 4 ? (
            <button onClick={next} disabled={!canContinue}
              className="pm-btn-primary h-9 px-3 rounded-lg text-[12px] font-bold flex items-center gap-1.5 disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="builder-next-btn">
              Continue <ChevronRight size={11} />
            </button>
          ) : (
            <button onClick={generate} disabled={generating}
              className="pm-btn-primary h-9 px-4 rounded-lg text-[12px] font-bold flex items-center gap-1.5"
              data-testid="builder-generate-btn">
              {generating ? <Loader2 size={11} className="animate-spin" /> : <FileText size={11} />}
              {generating ? "Generating…" : "Generate PDF"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────── Small components ───────────────────────────

function KV({ k, v }) {
  return (
    <div className="flex">
      <div className="w-24 text-slate-500 text-[11px] uppercase tracking-wider font-bold">{k}</div>
      <div className="flex-1 font-bold text-slate-800 capitalize">{String(v)}</div>
    </div>
  );
}

function TierBadge({ tier }) {
  const t = (tier || "client").toLowerCase();
  const TONE = {
    client:   "bg-emerald-50 text-emerald-800 border-emerald-200",
    network:  "bg-blue-50 text-blue-700 border-blue-200",
    mandated: "bg-amber-50 text-amber-800 border-amber-200",
  };
  const LABEL = { client: "CLIENT", network: "NETWORK", mandated: "MANDATE" };
  return <span className={`pm-pill ${TONE[t]} text-[9px] shrink-0`}>{LABEL[t] || "—"}</span>;
}

function TierFilterRow({ tiers, onToggle }) {
  const ALL = [
    { id: "client",   label: "Clients" },
    { id: "network",  label: "Network" },
    { id: "mandated", label: "Mandated" },
  ];
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-2">Tier filter</div>
      <div className="flex items-center gap-2">
        {ALL.map((t) => (
          <label key={t.id} className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg border border-slate-200 cursor-pointer">
            <input type="checkbox" checked={tiers.includes(t.id)} onChange={() => onToggle(t.id)} className="accent-emerald-600" data-testid={`tier-toggle-${t.id}`} />
            <span className="text-[12px] font-bold">{t.label}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

function PreviewSummary({ count, players }) {
  if (count === null) return <div className="text-[12px] text-slate-500"><Loader2 size={11} className="inline animate-spin mr-1" /> Counting…</div>;
  if (count === 0) return <div className="text-[12px] text-amber-700 flex items-center gap-1.5"><AlertTriangle size={11} /> No players match — adjust your filters.</div>;
  return (
    <div className="rounded-xl border border-slate-200 p-3" data-testid="preview-summary">
      <div className="flex items-center gap-2 mb-2">
        <Users size={13} className="text-emerald-600" />
        <div className="text-sm font-bold">{count} player{count === 1 ? "" : "s"} selected</div>
        <span className="text-[11px] text-slate-500">· ~{Math.ceil(count * 3 + 1)} pages · ~{Math.ceil(count * 1.5)}s to generate</span>
      </div>
      <div className="max-h-40 overflow-y-auto space-y-1">
        {players.slice(0, 12).map((p) => (
          <div key={p.id} className="flex items-center gap-2 text-[12px]">
            <span className="font-bold flex-1 truncate">{p.full_name}</span>
            <span className="text-slate-500 shrink-0">{p.position || "—"}</span>
            <span className="text-slate-400 shrink-0">{p.current_club || "—"}</span>
            <TierBadge tier={p.tier} />
          </div>
        ))}
        {players.length > 12 && <div className="text-[11px] text-slate-500 pt-1">+ {players.length - 12} more</div>}
      </div>
    </div>
  );
}


function AnalyticsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/reports/analytics/sends?limit=100")
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, []);

  const CHANNEL_ICONS = { email: "✉", whatsapp: "✆", sms: "✆", case: "▦", download: "↓" };

  return (
    <div className="pm-card overflow-hidden h-[calc(100vh-200px)] flex flex-col" data-testid="reports-analytics-panel">
      <div className="px-5 py-3 border-b border-slate-200">
        <div className="font-display text-lg font-bold tracking-tight">Send analytics</div>
        <div className="text-[11px] text-slate-500">Where your reports went and to whom — last 100 sends across the workspace.</div>
      </div>
      <div className="flex-1 overflow-y-auto p-5 space-y-5">
        {loading && <div className="text-slate-500 text-sm text-center py-10"><Loader2 size={14} className="inline animate-spin mr-2" /> Loading…</div>}
        {!loading && data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div className="rounded-xl border border-slate-200 px-3 py-3" data-testid="analytics-total">
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Total sends</div>
                <div className="font-display text-3xl font-black tracking-tight mt-1">{data.total ?? 0}</div>
              </div>
              {["email", "whatsapp", "sms", "case"].map((ch) => (
                <div key={ch} className="rounded-xl border border-slate-200 px-3 py-3" data-testid={`analytics-${ch}`}>
                  <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">{CHANNEL_ICONS[ch]} {ch}</div>
                  <div className="font-display text-3xl font-black tracking-tight mt-1">{data.by_channel?.[ch] ?? 0}</div>
                </div>
              ))}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-2">Recent sends</div>
              {(data.sends || []).length === 0 ? (
                <div className="text-[12px] text-slate-500">No sends recorded yet. Reports sent via the picker modal will show up here.</div>
              ) : (
                <div className="space-y-1.5">
                  {data.sends.slice(0, 30).map((s) => (
                    <div key={s.id} className="flex items-center gap-3 px-3 py-2 rounded-lg border border-slate-200" data-testid={`send-row-${s.id}`}>
                      <span className="font-bold text-[10px] uppercase tracking-wider text-slate-500 w-16 shrink-0">{s.channel}</span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold truncate">{s.report_title || s.report_id}</div>
                        <div className="text-[11px] text-slate-500 truncate">→ {s.recipient} · {s.agent_name || ""}</div>
                      </div>
                      <span className="text-[10px] text-slate-400 shrink-0 font-mono-tab">{fmtDateTime(s.sent_at).slice(0, 16)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
